import type { Editor } from '@tiptap/core';
import { Dropdown } from 'antd';
import type { MenuProps } from 'antd';
import { UnorderedListOutlined, OrderedListOutlined, CheckSquareOutlined } from '@ant-design/icons';

interface ListDropdownMenuProps {
  editor: Editor;
}

export const ListDropdownMenu = ({ editor }: ListDropdownMenuProps) => {
  const getCurrentListType = (): string | null => {
    if (editor.isActive('bulletList')) {
      return 'bullet';
    }
    if (editor.isActive('orderedList')) {
      return 'ordered';
    }
    if (editor.isActive('taskList')) {
      return 'task';
    }
    return null;
  };

  const setList = (type: 'bullet' | 'ordered' | 'task') => {
    if (!editor.isEditable) return;
    if (type === 'bullet') {
      editor.chain().focus().setParagraph().toggleBulletList().run();
    } else if (type === 'ordered') {
      editor.chain().focus().setParagraph().toggleOrderedList().run();
    } else if (type === 'task') {
      editor.chain().focus().setParagraph().toggleTaskList().run();
    }
  };

  const removeList = () => {
    if (!editor.isEditable) return;
    editor.chain().focus().liftListItem('listItem').run();
  };

  const currentType = getCurrentListType();

  // Determine display text and icon based on current list type
  const getDisplayInfo = () => {
    switch (currentType) {
      case 'bullet':
        return { text: 'Bullet List', icon: <UnorderedListOutlined /> };
      case 'ordered':
        return { text: 'Numbered List', icon: <OrderedListOutlined /> };
      case 'task':
        return { text: 'Task List', icon: <CheckSquareOutlined /> };
      default:
        return { text: 'List', icon: <UnorderedListOutlined /> };
    }
  };

  const displayInfo = getDisplayInfo();

  const menuItems: MenuProps['items'] = [
    {
      key: 'bullet',
      label: 'Bullet List',
      icon: <UnorderedListOutlined />,
      onClick: () => setList('bullet')
    },
    {
      key: 'ordered',
      label: 'Numbered List',
      icon: <OrderedListOutlined />,
      onClick: () => setList('ordered')
    },
    {
      key: 'task',
      label: 'Task List',
      icon: <CheckSquareOutlined />,
      onClick: () => setList('task')
    },
    {
      type: 'divider'
    },
    {
      key: 'remove',
      label: 'Remove List',
      onClick: removeList
    }
  ];

  return (
    <Dropdown.Button
      menu={{ items: menuItems }}
      type={currentType ? 'primary' : 'default'}
      title="List"
      icon={displayInfo.icon}
      disabled={!editor.isEditable}
    >
      {displayInfo.text}
    </Dropdown.Button>
  );
};
